import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import LabeledInput from 'modules/core/components/LabeledInput'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { matchText, normalizeText } from 'modules/core/lib/stringUtils'

import Blank from 'modules/employees/components/Selector/Blank/index'
import Container from 'modules/employees/components/Selector/Container/index'
import type { PrivateProps as OptionProps } from 'modules/employees/components/Selector/Option/index'

import SearchInput from './SearchInput'

type LabeledInputProps = React.ComponentProps<typeof LabeledInput>
type InheritedProps = Pick<LabeledInputProps, 'warning'>

export interface Props extends InheritedProps {
  placeholder: string
  label?: string
  mandatory?: boolean
  isDisabled?: boolean
  isOpen?: boolean
  error?: string
  value: unknown[]
  onChange: (value: unknown[] | unknown | null) => void
  children: Array<React.ReactElement<OptionProps>>
  blankAction?: (value: string) => void
  actionLabel?: string
  onToggle?: (isOpen: boolean) => void
  canCreate?: boolean
}

const Popover: React.FC<Props> = ({
  children,
  mandatory,
  value,
  label,
  placeholder,
  isDisabled,
  warning,
  onChange,
  isOpen = false,
  error,
  blankAction,
  actionLabel,
  onToggle,
  canCreate = true,
}) => {
  const [open, setOpen] = React.useState(isOpen)
  const { search, debouncedSearch, onSearch, clearSearch } = useDebouncedSearch()

  const options = React.Children.toArray(children) as Array<React.ReactElement<OptionProps>>
  let inputRef: HTMLInputElement | null = null
  const getInputRef: React.RefCallback<HTMLInputElement> = (ref) => {
    inputRef = ref
  }

  useEffect(() => {
    if (isDisabled) return

    setOpen(isOpen)
  }, [isOpen, isDisabled, inputRef])

  const valueLabel = (() => {
    return options.reduce<string>((acc, x) => {
      if (!value.includes(x.props.value)) return acc

      return acc + (acc ? ', ' : '') + x.props.label
    }, '')
  })()

  const handleToggle = (isOpen: boolean) => {
    if (onToggle) onToggle(isOpen)

    if (!isOpen) clearSearch()
  }

  const handleChange = (value: unknown[] | unknown) => {
    onChange(value)
  }

  const handleClear = () => {
    if (!!search && open) {
      clearSearch()
    } else {
      handleChange(null)
      clearSearch()
    }
  }

  const filteredChildren = useUnsafeMemo(() => {
    if (!children) return []

    return children.filter((x) => matchText(debouncedSearch, x.props.searchValue))
  }, [debouncedSearch, children])

  const searchIsEqual = filteredChildren.filter(
    (x) => normalizeText(x.props.searchValue) === normalizeText(search)
  ).length

  const hasNoOptions = options.length === 0
  const showBlank =
    canCreate &&
    blankAction &&
    (hasNoOptions || (!!search && !searchIsEqual))
  const showContent = !!children.length || !!search || (hasNoOptions && canCreate && blankAction)

  return (
    <Box width="full">
      <PopoverMenu type="dialog" onClick={handleToggle} open={open}>
        <PopoverMenu.Trigger disabled={isDisabled}>
          <LabeledInput
            label={label}
            mandatory={mandatory}
            warning={warning}
            readonly={isDisabled}
            error={error}
          >
            <SearchInput
              refInput={getInputRef}
              placeholder={placeholder}
              valueLabel={valueLabel}
              isOpen={open}
              search={search}
              onSearch={onSearch}
              clearSearch={handleClear}
            />
          </LabeledInput>
        </PopoverMenu.Trigger>
        <PopoverMenu.Content
          side="bottom"
          align="center"
          width={showContent ? 's400' : 's0'}
          focusOnOpen={false}
        >
          {showBlank && (
            <Blank search={search} actionLabel={actionLabel} action={blankAction} />
          )}
          <Container onChange={handleChange} value={value} filteredChildren={filteredChildren}>
            {children}
          </Container>
        </PopoverMenu.Content>
      </PopoverMenu>
    </Box>
  )
}

export default Popover
